import api from './client'
import type { DiveCenter, PaginatedResponse } from '@/types'

export type ShiftStatus = 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED'

// Notifica aggregata del centro (Blocco D): un'azione-utente verso il centro.
export type CenterNotificationType =
  | 'excursion_booking' | 'course_request' | 'staff_request' | 'review'

export interface CenterNotification {
  id: string
  type: CenterNotificationType
  at: string
  userId: string
  title: string
  meta: Record<string, unknown>
  unread: boolean
}

export type StaffRoleType =
  | 'LEGAL_OWNER' | 'OWNER' | 'MANAGER'
  | 'INSTRUCTOR' | 'GUIDE' | 'DIVEMASTER'
  | 'BOAT_CAPTAIN' | 'ASSISTANT' | 'STAFF'

export interface StaffMember {
  id: string
  centerId: string
  userId: string
  role: StaffRoleType
  additionalRoles: StaffRoleType[]
  certifications: string[]
  isActive: boolean
  joinedAt: string
  startDate: string | null
  endDate: string | null
}

export interface CourseAssistant {
  id: string
  courseId: string
  staffId: string
  role: 'ASSISTANT_INSTRUCTOR' | 'TRAINEE'
  assignedAt: string
}

export interface InternshipEntry {
  id: string
  workDate: string
  hours: number
  activity: string
  signedAt: string | null
}

export interface InternshipSummary {
  entries: InternshipEntry[]
  totalHours: number
  signedHours: number
  pendingHours: number
}

export type StaffRequestStatus    = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED'
export type StaffRequestInitiator = 'USER' | 'CENTER'

export type PendingStaffContactStatus = 'PENDING' | 'RECONCILED'

export interface PendingStaffContact {
  id: string
  centerId: string
  createdByUserId: string
  firstName: string
  lastName: string
  email: string | null
  phone: string | null
  requestedRole: StaffRoleType
  notes: string | null
  status: PendingStaffContactStatus
  reconciledUserId: string | null
  reconciledAt: string | null
  createdAt: string
}

export interface StaffRequest {
  id: string
  centerId: string
  userId: string
  requestedRole: StaffRoleType
  initiatedBy: StaffRequestInitiator
  status: StaffRequestStatus
  note: string | null
  resolvedBy: string | null
  createdAt: string
  resolvedAt: string | null
}

export type AffiliationStatus = 'PENDING' | 'VERIFIED' | 'EXPIRED'

export interface CoursePackage {
  id: string
  centerId: string
  name: string
  type: string
  certificationAgency: string | null
  price: number | null
  currency: string
  duration: number | null
  maxStudents: number | null
  includes: string[]
  description: string | null
  active: boolean
  createdAt: string
}

/** Una classe programmata: sotto e' un Course del centro, riesposto in forma ridotta. */
export interface CourseSchedule {
  id: string
  title: string
  level: string | null
  startDate: string | null
  endDate: string | null
  maxStudents: number | null
  isActive: boolean
}

/** Iscrizione a un corso del centro. Non contiene indirizzi: solo l'identificativo. */
export interface CourseStudent {
  id: string
  userId: string
  status: string
  enrolledAt: string
  completedAt: string | null
  course: { id: string; title: string; level: string | null }
}

export interface IssuedCertification {
  id: string
  userId: string
  certificationNumber: string | null
  certificationIssuedAt: string | null
  course: { title: string; level: string | null; certificationName: string | null }
}

export interface CenterAffiliation {
  id: string
  centerId: string
  agencyCode: string
  affiliationType: string
  certNumber: string | null
  imageUrl: string | null
  status: AffiliationStatus
  verifiedBy: string | null
  verifiedAt: string | null
  expiresAt: string | null
  createdAt: string
}

export type ExcursionStatus = 'DRAFT' | 'PUBLISHED' | 'FULL' | 'COMPLETED' | 'CANCELLED'
export type ExcursionBookingStatus = 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED'

export interface Excursion {
  id: string
  centerId: string
  guideStaffId: string | null
  diveSiteId: string | null
  diveSiteName: string | null
  title: string
  description: string | null
  startsAt: string
  durationMin: number
  maxDivers: number
  minCertLevel: string | null
  priceEuroCents: number
  equipmentIncluded: boolean
  status: ExcursionStatus
  notes: string | null
  audience: 'PUBLIC' | 'RESTRICTED'
  visibleFrom: string | null
  visibleUntil: string | null
  createdAt: string
  bookings?: ExcursionBooking[]
}

export interface ExcursionInvite {
  id: string
  excursionId: string
  userId: string
  invitedBy: string | null
  createdAt: string
}

export interface ExcursionBooking {
  id: string
  excursionId: string
  userId: string
  status: ExcursionBookingStatus
  paidEuroCents: number
  diveLogEntryId: string | null
  bookedAt: string
  confirmedAt: string | null
  cancelledAt: string | null
}

export interface CreateExcursionData {
  guideStaffId?: string
  diveSiteId?: string
  diveSiteName?: string
  title: string
  description?: string
  startsAt: string
  durationMin: number
  maxDivers: number
  minCertLevel?: string
  priceEuroCents?: number
  equipmentIncluded?: boolean
  notes?: string
  audience?: 'PUBLIC' | 'RESTRICTED'
  visibleFrom?: string | null
  visibleUntil?: string | null
  inviteUserIds?: string[]
}

export interface HireStaffData {
  userId: string
  role: StaffRoleType
  startDate?: string          // ISO date YYYY-MM-DD
  endDate?: string | null     // ISO date, null = open-ended
  note?: string
}

export interface StaffShift {
  id: string
  centerId: string
  staffId: string
  shiftDate: string
  startTime: string
  endTime: string
  activity: string | null
  notes: string | null
  status: ShiftStatus
  createdBy: string
  createdAt: string
  updatedAt: string
  staff?: { id: string; userId: string; role: string }
}

export interface CreateShiftData {
  staffId: string
  shiftDate: string
  startTime: string
  endTime: string
  activity?: string
  notes?: string
}

export type ActivityLogType = 'DIVE' | 'COURSE' | 'INCIDENT' | 'MAINTENANCE' | 'OTHER'

export interface ActivityLog {
  id: string
  centerId: string
  type: ActivityLogType
  description: string
  date: string
  authorId: string | null
  authorName: string | null
  notes: string | null
  createdAt: string
}

export interface Review {
  id: string
  centerId: string
  userId: string
  bookingId?: string | null
  rating: number
  title?: string | null
  body?: string | null
  isVerified: boolean
  ownerReply?: string | null
  ownerRepliedAt?: string | null
  createdAt: string
}

export type EquipmentStatus = 'AVAILABLE' | 'RENTED' | 'MAINTENANCE' | 'RETIRED'
export type TankMaterial = 'STEEL' | 'ALUMINIUM'
export type LoyaltyTier = 'BRONZE' | 'SILVER' | 'GOLD' | 'PLATINUM'

export interface Equipment {
  id: string
  centerId: string
  type: string
  brand?: string | null
  model?: string | null
  serialNumber?: string | null
  status: EquipmentStatus
  purchaseDate?: string | null
  lastServiceDate?: string | null
  nextServiceDate?: string | null
  sizeCode?: string | null
  notes?: string | null
  createdAt: string
}

export interface Tank {
  id: string
  centerId: string
  serialNumber: string
  volumeL: number
  material: TankMaterial
  valveType?: string | null
  currentPressureBar: number
  lastFillDate?: string | null
  lastHydroDate?: string | null
  nextHydroDate?: string | null
  isAvailable: boolean
  notes?: string | null
  createdAt: string
}

export interface Boat {
  id: string
  centerId: string
  name: string
  registration?: string | null
  capacityPersons: number
  engineType?: string | null
  fuelCapacityL?: number | null
  lastMaintenanceDate?: string | null
  nextMaintenanceDate?: string | null
  isActive: boolean
  notes?: string | null
  createdAt: string
  // Extended fields
  boatType?: string | null
  lengthM?: number | null
  beamM?: number | null
  draftM?: number | null
  maxSpeedKnots?: number | null
  navigationLimit?: string | null
  homePort?: string | null
  yearBuilt?: number | null
  // Relations (included on list endpoint)
  insurances?: BoatInsurance[]
  maintenanceLogs?: BoatMaintenanceLog[]
  _count?: { safetyEquipment: number; usageLogs: number }
}

export interface BoatInsurance {
  id: string
  boatId: string
  policyNumber: string
  insurer: string
  coverageType: string
  startDate: string
  expiryDate: string
  premiumCents?: number | null
  currency: string
  documentKey?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
}

export interface BoatSafetyEquipment {
  id: string
  boatId: string
  name: string
  category: string
  quantity: number
  serialNumber?: string | null
  expiryDate?: string | null
  lastCheckedDate?: string | null
  notes?: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface BoatMaintenanceLog {
  id: string
  boatId: string
  maintenanceType: string
  performedAt: string
  performedBy?: string | null
  costCents?: number | null
  currency: string
  notes?: string | null
  documentKey?: string | null
  nextDueDate?: string | null
  createdAt: string
}

export interface BoatUsageLog {
  id: string
  boatId: string
  linkedType: string
  linkedId: string
  date: string
  captainUserId?: string | null
  passengersCount?: number | null
  fuelUsedL?: number | null
  distanceNm?: number | null
  notes?: string | null
  createdAt: string
}

export type CalendarEntryStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED'
export type TripType = 'BOAT' | 'SHORE'
export type DiveType = 'RECREATIONAL' | 'TECHNICAL' | 'CCR' | 'SIDEMOUNT'
export type ParticipantRole = 'CAPTAIN' | 'GUIDE' | 'DIVEMASTER' | 'INSTRUCTOR' | 'PARTICIPANT' | 'CREW' | 'CONTROLLER' | 'SAFETY_DIVER'

/** Avviso di una regola in shadow: l'azione è permessa, l'interfaccia lo mostra. */
export interface RuleWarning {
  rule: string
  code?: string
  message?: string
  details?: unknown
  userId?: string | null
}

export interface ExperienceClass { id?: string; code: string; label: string; sortOrder: number; requiresCheckDive: boolean }

export interface OnboardSafety {
  checkedAt: string
  tanks: { id: string; serialNumber: string | null; nextHydroDate: string | null; state: 'EXPIRED' | 'DUE_SOON' | 'OK' | 'UNKNOWN' }[]
  tankCounts: { total: number; expired: number; dueSoon: number; unknown: number }
  oxygen: { kind: string; lastCheckedAt: string | null; nextCheckDue: string | null; overdue: boolean | null }
  firstAid: { kind: string; lastCheckedAt: string | null; nextCheckDue: string | null; overdue: boolean | null }
  alarms: ('TANK_HYDRO_EXPIRED' | 'OXYGEN_KIT_OVERDUE' | 'OXYGEN_KIT_NEVER_CHECKED' | 'FIRST_AID_KIT_OVERDUE')[]
  alarm: boolean
}

export interface MissingDiverProcedure {
  participantId?: string
  participantName: string
  startedAt: string
  lastSeenAt?: string
  lastSeenNote?: string
  steps: Record<string, { at: string; byUserId: string; note?: string }>
  outcome?: string
  closedAt?: string
}
export interface MissingDiverView {
  steps: string[]
  procedure: MissingDiverProcedure | null
  missing: string[]
  reviewed: boolean
  calendarEntryId?: string | null
  sosAlertId?: string | null
}
export interface IncidentProtocolView { steps: string[]; protocol: Record<string, boolean>; missing: string[]; updatedAt: string | null }

export interface CalendarParticipant {
  id: string
  diveEntryId: string
  userId?: string | null
  name: string
  role: ParticipantRole
  certificationLevel?: string | null
  tankId?: string | null
  isConfirmed: boolean
  notes?: string | null
  briefingAcknowledgedAt?: string | null
  ecoAwarenessCheckedAt?: string | null
  ecoCodeSignedAt?: string | null
  ecoCodeSignedName?: string | null
}

export interface CalendarParticipantInput {
  name: string
  role?: ParticipantRole
  userId?: string
  certificationLevel?: string
  tankId?: string
  isConfirmed?: boolean
  notes?: string
}

export interface ManifestRow {
  id: string
  userId?: string | null
  name: string
  role: string
  status: 'NOT_CHECKED_IN' | 'IN_WATER' | 'OVERDUE' | 'RETURNED'
  briefingDone: boolean
  medical: 'VALID' | 'EXPIRED' | 'MISSING' | 'UNKNOWN'
}

export interface BoatManifest {
  entry: { id: string; title: string | null; diveSiteName: string | null; maxDepthM: number | null }
  staff: ManifestRow[]
  participants: ManifestRow[]
  crew: ManifestRow[]
  counts: { onBoard: number; staff: number; participants: number; notCheckedIn: number; inWater: number; overdue: number; returned: number }
  warnings: { noStaff: boolean; briefingMissing: string[]; medicalMissing: string[] }
  crewWarnings?: RuleWarning[]
  checkDivePending?: string[]
}

export interface DivePlanData {
  gasO2Percent?: number | string | null
  turnPressureBar?: number | null
  reservePressureBar?: number | null
  safetyStopDepthM?: number | string | null
  safetyStopMin?: number | null
  alternateSite?: string | null
  emergencyPlan?: string | null
  risks?: Record<string, { level: 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH'; mitigation?: string }> | null
  whatIf?: { scenario: string; response: string }[] | null
}

export interface DivePlanResponse {
  plan: DivePlanData | null
  warnings: string[]
  riskWarnings: { code: string; classes: string[] }[]
}

export interface BoatHandoverResponse {
  handover: { items: Record<string, boolean>; captainName?: string | null; notes?: string | null; signedAt?: string | null } | null
  items: string[]
  missing: string[]
}

export interface Debriefing {
  id: string
  topics: Record<string, boolean>
  notes: string | null
}

export interface BriefingTemplates {
  kinds: string[]
  phases: { BEFORE: string[]; DURING: string[]; AFTER: string[] }
}

export interface CalendarEntry {
  id: string
  centerId: string
  boatId?: string | null
  tripType: TripType
  title?: string | null
  diveSiteId?: string | null
  diveSiteName?: string | null
  latitude?: number | null
  longitude?: number | null
  scheduledDate: string
  departureTime?: string | null
  estimatedReturnTime?: string | null
  diveType: DiveType
  maxDepthM?: number | null
  plannedBottomTimeMin?: number | null
  maxParticipants?: number | null
  minCertificationAgency?: string | null
  minCertificationCode?: string | null
  status: CalendarEntryStatus
  postDiveNotes?: string | null
  notes?: string | null
  createdAt: string
  updatedAt: string
  boat?: { id: string; name: string; registration?: string | null; capacityPersons: number } | null
  participants?: CalendarParticipant[]
}

export interface CreateCalendarEntryData {
  scheduledDate: string
  tripType?: TripType
  boatId?: string
  title?: string
  diveSiteId?: string
  diveSiteName?: string
  departureTime?: string
  estimatedReturnTime?: string
  diveType?: DiveType
  maxDepthM?: number
  plannedBottomTimeMin?: number
  maxParticipants?: number
  minCertificationAgency?: string
  minCertificationCode?: string
  notes?: string
}

export interface LoyaltyAccount {
  userId: string
  tier: LoyaltyTier
  pointsBalance: number
  totalEarned: number
  totalSpent: number
  createdAt: string
}

export interface LoyaltyTransaction {
  id: string
  type: string
  points: number
  note: string | null
  createdAt: string
}

export interface LoyaltyReward {
  id: string
  name: string
  descriptionIt: string | null
  pointsCost: number
  minTier: LoyaltyTier
  stock: number | null
  isActive: boolean
}

export interface MyLoyaltyEntry {
  centerId: string
  centerName: string
  tier: LoyaltyTier
  pointsBalance: number
  totalEarned: number
  tierProgress: { current: LoyaltyTier; next: LoyaltyTier | null; pointsToNext: number | null }
  recentTransactions: LoyaltyTransaction[]
}

export interface CenterKPI {
  totalCustomers: number
  totalBookingsThisMonth: number
  totalRevenueEuroCentsThisMonth: number
  availableEquipmentCount: number
  availableTankCount: number
  tanksNeedingHydroCount: number
  boatsInService: number
  upcomingTripsCount: number
  completedCoursesThisMonth: number
  incidentsThisMonth: number
  expiredMedCertsCount: number
}

export type CourseLevel = 'DISCOVER' | 'OPEN_WATER' | 'ADVANCED' | 'RESCUE' | 'DIVEMASTER' | 'INSTRUCTOR' | 'SPECIALTY' | 'REBREATHER_NODECO' | 'REBREATHER_DECO_45' | 'REBREATHER_DECO_60' | 'REBREATHER_DECO_100' | 'REBREATHER_INSTRUCTOR'
export type EnrollmentStatus = 'ENROLLED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED' | 'FAILED'
export type MedicalCertType = 'AGONISTIC' | 'NON_AGONISTIC' | 'SELF_DECLARATION'
export type MedicalCertStatus = 'VALID' | 'EXPIRED' | 'PENDING_RENEWAL'
export type IncidentSeverity = 'MINOR' | 'MODERATE' | 'SERIOUS' | 'CRITICAL'
export type IncidentStatus = 'OPEN' | 'UNDER_REVIEW' | 'RESOLVED' | 'CLOSED'

export interface Course {
  id: string
  centerId: string
  instructorStaffId?: string | null
  title: string
  titleEn?: string | null
  description?: string | null
  level: CourseLevel
  agencyCode?: string | null
  certificationCode?: string | null
  certificationName?: string | null
  maxStudents?: number | null
  priceEuroCents?: number | null
  durationDays?: number | null
  startDate?: string | null
  endDate?: string | null
  isActive: boolean
  enrolledCount?: number
  createdAt: string
  updatedAt: string
}

export interface CourseEnrollment {
  id: string
  courseId: string
  userId: string
  customerId?: string | null
  status: EnrollmentStatus
  enrolledAt: string
  completedAt?: string | null
  certificationIssuedAt?: string | null
  certificationNumber?: string | null
  paidEuroCents: number
}

export type CourseMaterialType = 'VIDEO' | 'PDF' | 'LINK' | 'DOCUMENT'
export type SessionType = 'THEORY' | 'POOL' | 'OPEN_WATER' | 'EXAM' | 'BRIEFING'

export interface CourseMaterial {
  id: string
  courseId: string
  title: string
  type: CourseMaterialType
  url: string
  description?: string | null
  sortOrder: number
  createdAt: string
}

export interface CourseSession {
  id: string
  courseId: string
  title: string
  type: SessionType
  scheduledAt: string
  durationMin?: number | null
  notes?: string | null
  conductedByStaffId?: string | null
  createdAt: string
  updatedAt: string
  evaluations?: SessionEvaluation[]
}

export interface SessionEvaluation {
  id: string
  sessionId: string
  enrollmentId: string
  score?: number | null
  passed?: boolean | null
  notes?: string | null
  skills?: Record<string, boolean> | null
  evaluatedAt: string
}

export interface CourseSessionWithTemplate extends CourseSession {
  skillTemplate: string[]
}

export interface StudentProgressSession {
  id: string
  type: SessionType
  title: string
  scheduledAt: string
  durationMin?: number | null
  evaluation: SessionEvaluation | null
}

export interface StudentProgressResponse {
  enrollment: {
    id: string
    studentId: string
    enrolledAt: string
    completedAt: string | null
    status: EnrollmentStatus
    certNumber: string | null
  }
  course: {
    id: string
    name: string
    certificationName: string | null
    level: CourseLevel
    agencyCode: string | null
  }
  sessions: StudentProgressSession[]
  summary: {
    totalSessions: number
    completedSessions: number
    passedSessions: number
    averageScore: number | null
    overallPassed: boolean
  }
  /** sessioni pratiche richieste dallo standard di agenzia (null se non previste) */
  practical?: { required: number | null; passed: number; remaining: number | null }
  /** briefing e debriefing condotti dal candidato con esito positivo */
  leadership?: { briefingsLed: number; debriefingsLed: number }
  /** soglia dell'esame dallo standard di agenzia, in percentuale (null se non definita) */
  examPassPercent?: number | null
}

export interface ExamQuestion {
  id: string
  courseId: string
  question: string
  options: string[]
  correctIdx: number
  explanation?: string | null
  order: number
  isActive: boolean
  createdAt: string
}

export type ExamSessionStatus = 'IN_PROGRESS' | 'SUBMITTED' | 'EXPIRED' | 'GRADED'

export interface ExamSessionSummary {
  id: string
  userId: string
  enrollmentId: string
  status: ExamSessionStatus
  score?: number | null
  passed?: boolean | null
  startedAt: string
  submittedAt?: string | null
  expiresAt?: string | null
}

export interface ExamAnswerDetail {
  questionId: string
  selectedIdx: number
  isCorrect: boolean
  question: { question: string; options: string[]; correctIdx: number; explanation?: string | null }
}

export interface ExamSessionDetail extends ExamSessionSummary {
  answers: ExamAnswerDetail[]
}

export interface ExamStartQuestion {
  id: string
  question: string
  options: string[]
  order: number
}

export interface ExamStartResponse {
  examSessionId: string
  expiresAt: string
  questions: ExamStartQuestion[]
}

export type ExamResultResponse = ExamSessionDetail

export interface CourseAnalyticsData {
  enrollmentsByStatus: Record<string, number>
  completionRate: number
  revenueTotal: number
  revenueByMonth: { month: string; revenue: number; count: number }[]
  avgExamScore: number | null
  examPassRate: number | null
}

export interface MedicalPolicy {
  medicalAlwaysRequired: boolean
  brevettoMakesOptional: boolean
  maxValidityMonths: number
  reminderFromMonths: number
  contraindicationBlock?: boolean
  isDefault?: boolean
  updatedAt?: string
}

export interface MedicalCertificate {
  id: string
  centerId: string
  userId: string
  customerId?: string | null
  type: MedicalCertType
  status: MedicalCertStatus
  issuedAt: string
  expiresAt: string
  issuedBy?: string | null
  notes?: string | null
  verifiedBy?: string | null
  verifiedAt?: string | null
  createdAt: string
}

export interface CustomerProfile {
  id: string
  centerId: string
  userId: string
  displayName?: string | null
  email?: string | null
  phone?: string | null
  whatsapp?: string | null
  certificationLevel?: string | null
  internalNotes?: string | null
  isVip: boolean
  experienceClassCode?: string | null
  checkDiveRequired?: boolean
  checkDiveDoneAt?: string | null
  checkDiveNotes?: string | null
  createdAt: string
  _count?: { bookings: number }
}

export interface DiveWaiver {
  id: string
  centerId: string
  userId: string
  customerId?: string | null
  bookingId?: string | null
  templateVersion: string
  signedAt: string
  ipAddress?: string | null
  signatureData?: string | null
  expiresAt?: string | null
  diverFullName?: string | null
  centerName?: string | null
  centerLogoUrl?: string | null
  formData?: string | null
  isMinor: boolean
  parentGuardianName?: string | null
  parentGuardianSignature?: string | null
  createdAt: string
}

export interface IncidentReport {
  id: string
  centerId: string
  calendarEntryId?: string | null
  reportedBy: string
  affectedUserId?: string | null
  severity: IncidentSeverity
  status: IncidentStatus
  title: string
  description: string
  actionsTaken?: string | null
  followUpNotes?: string | null
  resolvedAt?: string | null
  resolvedBy?: string | null
  occurredAt: string
  isMinorInvolved: boolean
  transmittedToAuthority: boolean
  transmittedAt?: string | null
  authorityProtocol?: string | null
  authorityNotes?: string | null
  inailReported: boolean
  inailReportedAt?: string | null
  inailProtocol?: string | null
  kind?: 'INCIDENT' | 'NEAR_MISS'
  qaRefType?: 'SAFETY_INCIDENT' | 'NEAR_MISS' | null
  qaRefId?: string | null
  sosAlertId?: string | null
  missingDiver?: MissingDiverProcedure | null
  createdAt: string
  updatedAt: string
}

export type SafetyKitKind = 'OXYGEN' | 'FIRST_AID' | 'COMPRESSOR_SERVICE' | 'AIR_QUALITY' | 'FILTER_CHANGE'
export const SAFETY_KIT_KINDS: SafetyKitKind[] = ['OXYGEN', 'FIRST_AID', 'COMPRESSOR_SERVICE', 'AIR_QUALITY', 'FILTER_CHANGE']

export interface SafetyKitCheck {
  id: string
  kind: SafetyKitKind
  checkedAt: string
  nextCheckDue: string
  notes?: string | null
}

export interface ChecklistAutoCheck {
  verified: boolean | null
  detail?: Record<string, unknown>
}

export interface SafetyChecklistItem {
  key?: string
  phase?: 'PRE_DEPARTURE' | 'ON_BOARD'
  label: string
  checked: boolean
  required?: boolean
  article?: string
}

export type GiftCardStatus = 'ACTIVE' | 'REDEEMED' | 'EXPIRED' | 'CANCELLED'

export interface GiftCard {
  id: string
  centerId: string
  code: string
  amountCents: number
  currency: string
  status: GiftCardStatus
  purchasedById: string
  recipientEmail: string | null
  recipientUserId: string | null
  message: string | null
  expiresAt: string
  purchasedAt: string
  redeemedAt: string | null
  center?: { id: string; name: string; city: string | null; country: string | null }
}

// Punto di immersione collegato a un centro (vista admin centro)
export interface CenterDiveSite {
  link_id: string
  id: string
  name: string
  description_it?: string | null
  country?: string | null
  region?: string | null
  depth_min_m?: number | null
  depth_max_m?: number | null
  dive_type?: string | null
  difficulty?: string | null
  is_verified?: boolean
  avg_rating?: number | null
  // stato del legame centro↔sito (modello ibrido): AUTO_PROXIMITY/CENTER/ADMIN, e conferma
  source?: 'AUTO_PROXIMITY' | 'CENTER' | 'ADMIN'
  verified?: boolean
  distance_km?: number | null
}

export const divecenterApi = {
  list: (params?: { page?: number; limit?: number; country?: string; q?: string; lat?: number; lng?: number; radiusKm?: number; type?: 'DIVE_CENTER' | 'DIVE_RESORT' | 'ASSOCIATION' }) =>
    api.get<PaginatedResponse<DiveCenter>>('/v1/divecenter/centers', { params }).then((r) => r.data),

  get: (id: string) => api.get<DiveCenter>(`/v1/divecenter/centers/${id}`).then((r) => r.data),

  // Punti di immersione che il centro propone (owner/manager): gestiti via divecenter,
  // che colloquia internamente con il servizio divesites (DiveCenterLink).
  listDiveSites: (centerId: string) =>
    api.get<{ items: CenterDiveSite[]; total: number }>(`/v1/divecenter/centers/${centerId}/dive-sites`).then((r) => r.data),
  addDiveSite: (centerId: string, diveSiteId: string, diveSiteName?: string) =>
    api.post(`/v1/divecenter/centers/${centerId}/dive-sites`, { diveSiteId, diveSiteName }).then((r) => r.data),
  removeDiveSite: (centerId: string, diveSiteId: string) =>
    api.delete(`/v1/divecenter/centers/${centerId}/dive-sites/${diveSiteId}`),

  create: (data: Partial<DiveCenter>) =>
    api.post<DiveCenter>('/v1/divecenter/centers', data).then((r) => r.data),

  update: (id: string, data: Partial<DiveCenter>) =>
    api.put<DiveCenter>(`/v1/divecenter/centers/${id}`, data).then((r) => r.data),

  delete: (id: string) =>
    api.delete(`/v1/divecenter/centers/${id}`),

  import: (rows: Partial<DiveCenter>[]) =>
    api.post<{ created: number; updated: number; errors: number; total: number }>('/v1/divecenter/centers/import', rows).then((r) => r.data),

  claim: (id: string) =>
    api.post<{ centerId: string; staffId: string }>(`/v1/divecenter/centers/${id}/claim`).then((r) => r.data),

  release: (id: string) =>
    api.post(`/v1/divecenter/centers/${id}/release`).then((r) => r.data),

  submitClaimRequest: (centerId: string, note?: string) =>
    api.post<{ claimRequestId: string; status: string }>(`/v1/divecenter/centers/${centerId}/claim-request`, { note }).then((r) => r.data),

  adminListCenters: (params?: { q?: string; country?: string; city?: string; claimStatus?: string; centerType?: string; page?: number; limit?: number }) =>
    api.get<{ data: DiveCenter[]; total: number; page: number; limit: number }>('/v1/divecenter/centers/admin-list', { params }).then((r) => r.data),

  convertType: (centerId: string, centerType: 'DIVE_CENTER' | 'DIVE_RESORT' | 'ASSOCIATION', associationType?: 'ASD' | 'APS' | 'ODV' | 'OTHER') =>
    api.patch<DiveCenter>(`/v1/divecenter/centers/${centerId}/convert-type`, { centerType, associationType }).then((r) => r.data),

  claimRequests: {
    list: (params?: { status?: string; page?: number; limit?: number }) =>
      api.get<{ data: DiveCenterClaimRequest[]; total: number }>('/v1/divecenter/claim-requests', { params }).then((r) => r.data),
    approve: (reqId: string, reviewNote?: string) =>
      api.patch(`/v1/divecenter/claim-requests/${reqId}/approve`, { reviewNote }).then((r) => r.data),
    reject: (reqId: string, reviewNote?: string) =>
      api.patch(`/v1/divecenter/claim-requests/${reqId}/reject`, { reviewNote }).then((r) => r.data),
  },

  myCenter: () =>
    api.get<DiveCenter & { myRole?: string; myAdditionalRoles?: string[] }>('/v1/divecenter/my', { _skipToast: true } as any).then((r) => r.data).catch(() => null),

  // Centro personale dell'istruttore freelance (GAP 2): crea/restituisce
  // l'unico centro centerType=PERSONAL dell'utente sotto cui pubblica escursioni.
  personalCenter: {
    get: () =>
      api.get<DiveCenter>('/v1/divecenter/me/personal-center', { _skipToast: true } as any)
        .then((r) => r.data).catch(() => null),
    ensure: (data?: { name?: string; country?: string; city?: string }) =>
      api.post<DiveCenter & { alreadyExisted?: boolean }>('/v1/divecenter/me/personal-center', data ?? {})
        .then((r) => r.data),
  },

  myCenters: () =>
    api.get<{ centers: Array<{ id: string; name: string; city?: string | null; country?: string | null; logoUrl?: string | null; role?: string | null }> }>('/v1/divecenter/my-centers', { _skipToast: true } as any)
      .then((r) => r.data.centers)
      .catch(() => []),

  kpi: (centerId: string) =>
    api.get<CenterKPI>(`/v1/divecenter/centers/${centerId}/kpi`).then((r) => r.data).catch(() => null),

  trips: (centerId: string, boatId: string, params?: { page?: number; limit?: number }) =>
    api.get(`/v1/divecenter/centers/${centerId}/boats/${boatId}/trips`, { params }).then((r) => r.data).catch(() => ({ data: [], total: 0 })),

  createTrip: (centerId: string, boatId: string, data: object) =>
    api.post(`/v1/divecenter/centers/${centerId}/boats/${boatId}/trips`, data).then((r) => r.data),

  bookings: {
    list: (centerId: string, params?: { page?: number; limit?: number; status?: string; from?: string; to?: string }) =>
      api.get<{ data: Array<{ id: string; customerId: string; type: string; scheduledAt: string; status: string; priceEuroCents: number; instructorStaffId: string | null; notes: string | null; customer?: { userId: string; certificationLevel: string | null } }>; total: number }>(`/v1/divecenter/centers/${centerId}/bookings`, { params }).then((r) => r.data).catch(() => ({ data: [], total: 0 })),
    update: (centerId: string, bookingId: string, data: { instructorStaffId?: string | null; status?: string; notes?: string }) =>
      api.put(`/v1/divecenter/centers/${centerId}/bookings/${bookingId}`, data).then((r) => r.data),
    assignInstructor: (centerId: string, bookingId: string, instructorStaffId: string | null) =>
      api.patch(`/v1/divecenter/centers/${centerId}/bookings/${bookingId}/instructor`, { instructorStaffId }).then((r) => r.data),
  },

  certifyDive: (centerId: string, data: { diveId: string; userId: string }) =>
    api.post(`/v1/divecenter/centers/${centerId}/certify-dive`, data).then((r) => r.data),

  equipment: {
    list: (centerId: string, params?: { type?: string; status?: string }) =>
      api.get<Equipment[]>(`/v1/divecenter/centers/${centerId}/equipment`, { params }).then((r) => r.data),
    create: (centerId: string, data: Partial<Equipment>) =>
      api.post<Equipment>(`/v1/divecenter/centers/${centerId}/equipment`, data).then((r) => r.data),
    update: (centerId: string, eqId: string, data: Partial<Equipment>) =>
      api.put<Equipment>(`/v1/divecenter/centers/${centerId}/equipment/${eqId}`, data).then((r) => r.data),
    delete: (centerId: string, eqId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/equipment/${eqId}`),
    assign: (centerId: string, eqId: string, data: { bookingId: string; notes?: string }) =>
      api.post(`/v1/divecenter/centers/${centerId}/equipment/${eqId}/assign`, data).then((r) => r.data),
    return: (centerId: string, eqId: string) =>
      api.post(`/v1/divecenter/centers/${centerId}/equipment/${eqId}/return`).then((r) => r.data),
  },

  shifts: {
    list: (centerId: string, params?: { date?: string; staffId?: string; status?: string }) =>
      api.get<StaffShift[]>(`/v1/divecenter/centers/${centerId}/shifts`, { params }).then((r) => r.data),
    my: (centerId: string, params?: { from?: string; to?: string }) =>
      api.get<StaffShift[]>(`/v1/divecenter/centers/${centerId}/shifts/my`, { params }).then((r) => r.data),
    create: (centerId: string, data: CreateShiftData) =>
      api.post<StaffShift>(`/v1/divecenter/centers/${centerId}/shifts`, data).then((r) => r.data),
    update: (centerId: string, shiftId: string, data: Partial<CreateShiftData & { status: ShiftStatus }>) =>
      api.patch<StaffShift>(`/v1/divecenter/centers/${centerId}/shifts/${shiftId}`, data).then((r) => r.data),
    delete: (centerId: string, shiftId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/shifts/${shiftId}`),
  },

  boats: {
    list: (centerId: string) =>
      api.get<Boat[]>(`/v1/divecenter/centers/${centerId}/boats`).then((r) => r.data),
    available: (centerId: string, date: string) =>
      api.get<Boat[]>(`/v1/divecenter/centers/${centerId}/boats/available`, { params: { date } }).then((r) => r.data),
    create: (centerId: string, data: Partial<Boat>) =>
      api.post<Boat>(`/v1/divecenter/centers/${centerId}/boats`, data).then((r) => r.data),
    update: (centerId: string, boatId: string, data: Partial<Boat>) =>
      api.put<Boat>(`/v1/divecenter/centers/${centerId}/boats/${boatId}`, data).then((r) => r.data),
    insurance: {
      list: (centerId: string, boatId: string) =>
        api.get<BoatInsurance[]>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/insurance`).then(r => r.data),
      create: (centerId: string, boatId: string, data: Partial<BoatInsurance>) =>
        api.post<BoatInsurance>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/insurance`, data).then(r => r.data),
      update: (centerId: string, boatId: string, insuranceId: string, data: Partial<BoatInsurance>) =>
        api.patch<BoatInsurance>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/insurance/${insuranceId}`, data).then(r => r.data),
      delete: (centerId: string, boatId: string, insuranceId: string) =>
        api.delete(`/v1/divecenter/centers/${centerId}/boats/${boatId}/insurance/${insuranceId}`),
    },
    safetyEquipment: {
      list: (centerId: string, boatId: string) =>
        api.get<BoatSafetyEquipment[]>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/safety-equipment`).then(r => r.data),
      create: (centerId: string, boatId: string, data: Partial<BoatSafetyEquipment>) =>
        api.post<BoatSafetyEquipment>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/safety-equipment`, data).then(r => r.data),
      update: (centerId: string, boatId: string, itemId: string, data: Partial<BoatSafetyEquipment>) =>
        api.patch<BoatSafetyEquipment>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/safety-equipment/${itemId}`, data).then(r => r.data),
      delete: (centerId: string, boatId: string, itemId: string) =>
        api.delete(`/v1/divecenter/centers/${centerId}/boats/${boatId}/safety-equipment/${itemId}`),
    },
    maintenance: {
      list: (centerId: string, boatId: string) =>
        api.get<BoatMaintenanceLog[]>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/maintenance`).then(r => r.data),
      create: (centerId: string, boatId: string, data: Partial<BoatMaintenanceLog>) =>
        api.post<BoatMaintenanceLog>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/maintenance`, data).then(r => r.data),
    },
    usage: (centerId: string, boatId: string, params?: { from?: string; to?: string }) =>
      api.get<BoatUsageLog[]>(`/v1/divecenter/centers/${centerId}/boats/${boatId}/usage`, { params }).then(r => r.data),
  },

  tanks: {
    list: (centerId: string) =>
      api.get<Tank[]>(`/v1/divecenter/centers/${centerId}/tanks`).then((r) => r.data),
    create: (centerId: string, data: Partial<Tank>) =>
      api.post<Tank>(`/v1/divecenter/centers/${centerId}/tanks`, data).then((r) => r.data),
    update: (centerId: string, tankId: string, data: Partial<Tank>) =>
      api.put<Tank>(`/v1/divecenter/centers/${centerId}/tanks/${tankId}`, data).then((r) => r.data),
    delete: (centerId: string, tankId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/tanks/${tankId}`),
    logFill: (centerId: string, tankId: string, data: { gasType: string; o2FractionPercent?: number; pressureBar: number }) =>
      api.post(`/v1/divecenter/centers/${centerId}/tanks/${tankId}/fills`, data).then((r) => r.data),
  },

  loyalty: {
    leaderboard: (centerId: string, params?: { tier?: string; limit?: number }) =>
      api.get<LoyaltyAccount[]>(`/v1/divecenter/centers/${centerId}/loyalty/admin/leaderboard`, { params }).then((r) => r.data),
    awardPoints: (centerId: string, userId: string, data: { points: number; note?: string }) =>
      api.post(`/v1/divecenter/centers/${centerId}/loyalty/admin/bonus`, { userId, ...data }).then((r) => r.data),
    myAccounts: () =>
      api.get<MyLoyaltyEntry[]>('/v1/divecenter/loyalty/my').then((r) => r.data).catch(() => []),
    centerAccount: (centerId: string) =>
      api.get<MyLoyaltyEntry>(`/v1/divecenter/centers/${centerId}/loyalty/account`).then((r) => r.data),
    centerRewards: (centerId: string) =>
      api.get<LoyaltyReward[]>(`/v1/divecenter/centers/${centerId}/loyalty/rewards`).then((r) => r.data).catch(() => []),
    redeem: (centerId: string, rewardId: string) =>
      api.post(`/v1/divecenter/centers/${centerId}/loyalty/rewards/${rewardId}/redeem`).then((r) => r.data),
    redemptions: (centerId: string) =>
      api.get<{ id: string; code: string; pointsSpent: number; createdAt: string; usedAt: string | null; expiresAt: string | null; reward: { name: string } }[]>(
        `/v1/divecenter/centers/${centerId}/loyalty/redemptions`
      ).then((r) => r.data).catch(() => []),
  },

  courses: {
    list: (centerId: string, params?: { level?: string; isActive?: string; page?: number; limit?: number }) =>
      api.get<{ data: Course[]; total: number; page: number; limit: number }>(`/v1/divecenter/centers/${centerId}/courses`, { params }).then((r) => r.data),
    create: (centerId: string, data: Partial<Course>) =>
      api.post<Course>(`/v1/divecenter/centers/${centerId}/courses`, data).then((r) => r.data),
    update: (centerId: string, courseId: string, data: Partial<Course>) =>
      api.put<Course>(`/v1/divecenter/centers/${centerId}/courses/${courseId}`, data).then((r) => r.data),
    deactivate: (centerId: string, courseId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/courses/${courseId}`),
    enrollments: (centerId: string, courseId: string) =>
      api.get<CourseEnrollment[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/enrollments`).then((r) => r.data),
    enroll: (centerId: string, courseId: string, data?: { userId?: string; customerId?: string; paidEuroCents?: number; notes?: string }) =>
      api.post<CourseEnrollment>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/enrollments`, data ?? {}).then((r) => r.data),
    updateEnrollment: (centerId: string, courseId: string, enrollmentId: string, data: Partial<CourseEnrollment>) =>
      api.patch<CourseEnrollment>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/enrollments/${enrollmentId}`, data).then((r) => r.data),
    issueCert: (centerId: string, courseId: string, enrollmentId: string, data?: { certificationNumber?: string }) =>
      api.post<CourseEnrollment>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/enrollments/${enrollmentId}/issue-cert`, data ?? {}).then((r) => r.data),
    myEnrollments: () =>
      api.get<CourseEnrollment[]>('/v1/divecenter/my-enrollments').then((r) => r.data),
    downloadCertificate: (enrollmentId: string) =>
      api.get(`/v1/divecenter/enrollments/${enrollmentId}/certificate`, { responseType: 'blob' }).then((r) => r.data as Blob),
  },

  materials: {
    list: (centerId: string, courseId: string) =>
      api.get<CourseMaterial[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/materials`).then((r) => r.data),
    create: (centerId: string, courseId: string, data: Omit<CourseMaterial, 'id' | 'courseId' | 'createdAt'>) =>
      api.post<CourseMaterial>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/materials`, data).then((r) => r.data),
    update: (centerId: string, courseId: string, materialId: string, data: Partial<CourseMaterial>) =>
      api.patch<CourseMaterial>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/materials/${materialId}`, data).then((r) => r.data),
    delete: (centerId: string, courseId: string, materialId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/courses/${courseId}/materials/${materialId}`),
  },

  briefings: {
    templates: (params?: { kinds?: string; maxDepthM?: number; currentKn?: number; visibility?: string; entryType?: string }) =>
      api.get<BriefingTemplates>('/v1/divecenter/briefings/templates', { params }).then((r) => r.data),
  },

  // Assistenti e tirocinanti di un corso, con le ore di tirocinio.
  courseTeam: {
    list: (centerId: string, courseId: string) =>
      api.get<CourseAssistant[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants`).then((r) => r.data),
    assign: (centerId: string, courseId: string, data: { staffId: string; role: CourseAssistant['role'] }) =>
      api.post<CourseAssistant>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants`, data).then((r) => r.data),
    remove: (centerId: string, courseId: string, assistantId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants/${assistantId}`),
    internship: (centerId: string, courseId: string, assistantId: string) =>
      api.get<InternshipSummary>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants/${assistantId}/internship`).then((r) => r.data),
    logHours: (centerId: string, courseId: string, assistantId: string, data: { workDate: string; hours: number; activity: string }) =>
      api.post<InternshipEntry>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants/${assistantId}/internship`, data).then((r) => r.data),
    signHours: (centerId: string, courseId: string, assistantId: string, entryId: string) =>
      api.patch<InternshipEntry>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/assistants/${assistantId}/internship/${entryId}/sign`).then((r) => r.data),
  },

  sessions: {
    list: (centerId: string, courseId: string) =>
      api.get<CourseSession[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/sessions`).then((r) => r.data),
    create: (centerId: string, courseId: string, data: Omit<CourseSession, 'id' | 'courseId' | 'createdAt' | 'updatedAt' | 'evaluations'>) =>
      api.post<CourseSession>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/sessions`, data).then((r) => r.data),
    update: (centerId: string, courseId: string, sessionId: string, data: Partial<CourseSession>) =>
      api.patch<CourseSession>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/sessions/${sessionId}`, data).then((r) => r.data),
    delete: (centerId: string, courseId: string, sessionId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/courses/${courseId}/sessions/${sessionId}`),
    saveEvaluation: (centerId: string, courseId: string, sessionId: string, data: { enrollmentId: string; score?: number; passed?: boolean; notes?: string; skills?: Record<string, boolean> }) =>
      api.post<SessionEvaluation>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/sessions/${sessionId}/evaluations`, data).then((r) => r.data),
    getProgress: (centerId: string, courseId: string, enrollmentId: string) =>
      api.get<StudentProgressResponse>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/enrollments/${enrollmentId}/progress`).then((r) => r.data),
  },

  customers: {
    list: (centerId: string, params?: { page?: number; limit?: number }) =>
      api.get<{ data: CustomerProfile[]; total: number }>(`/v1/divecenter/centers/${centerId}/customers`, { params }).then((r) => r.data),
    add: (centerId: string, data: { userId: string; displayName?: string; email?: string; certificationLevel?: string; internalNotes?: string; isVip?: boolean }) =>
      api.post<CustomerProfile>(`/v1/divecenter/centers/${centerId}/customers`, data).then((r) => r.data),
    remove: (centerId: string, custId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/customers/${custId}`),
    experienceClasses: (centerId: string) =>
      api.get<{ classes: ExperienceClass[] }>(`/v1/divecenter/centers/${centerId}/experience-classes`).then((r) => r.data.classes),
    saveExperienceClasses: (centerId: string, classes: Omit<ExperienceClass, 'id'>[]) =>
      api.put<{ classes: ExperienceClass[] }>(`/v1/divecenter/centers/${centerId}/experience-classes`, { classes }).then((r) => r.data.classes),
    setExperience: (centerId: string, custId: string, data: { experienceClassCode?: string | null; checkDiveRequired?: boolean }) =>
      api.put<CustomerProfile>(`/v1/divecenter/centers/${centerId}/customers/${custId}/experience`, data).then((r) => r.data),
    recordCheckDive: (centerId: string, custId: string, data: { done: boolean; notes?: string }) =>
      api.post<CustomerProfile>(`/v1/divecenter/centers/${centerId}/customers/${custId}/check-dive`, data).then((r) => r.data),
  },

  medical: {
    listCerts: (centerId: string, params?: { userId?: string; status?: string; page?: number; limit?: number }) =>
      api.get<{ data: MedicalCertificate[]; total: number }>(`/v1/divecenter/centers/${centerId}/medical/certs`, { params }).then((r) => r.data),
    customerMedical: (centerId: string, custId: string) =>
      api.get<{ certs: MedicalCertificate[]; waivers: DiveWaiver[] }>(`/v1/divecenter/centers/${centerId}/customers/${custId}/medical`).then((r) => r.data),
    addCert: (centerId: string, data: { userId: string; customerId?: string; type: MedicalCertType; issuedAt: string; expiresAt: string; issuedBy?: string; notes?: string }) =>
      api.post<MedicalCertificate>(`/v1/divecenter/centers/${centerId}/medical/certs`, data).then((r) => r.data),
    updateCert: (centerId: string, certId: string, data: Partial<MedicalCertificate>) =>
      api.patch<MedicalCertificate>(`/v1/divecenter/centers/${centerId}/medical/certs/${certId}`, data).then((r) => r.data),
    addWaiver: (centerId: string, data: {
      userId?: string; customerId?: string; bookingId?: string
      signatureData?: string; templateVersion?: string
      diverFullName?: string; centerLogoUrl?: string; formData?: string
      isMinor?: boolean; parentGuardianName?: string; parentGuardianSignature?: string
    }) =>
      api.post<DiveWaiver>(`/v1/divecenter/centers/${centerId}/waivers`, data).then((r) => r.data),
    listWaivers: (centerId: string, params?: { userId?: string; page?: number; limit?: number }) =>
      api.get<{ data: DiveWaiver[]; total: number }>(`/v1/divecenter/centers/${centerId}/waivers`, { params }).then((r) => r.data),
    getWaiver: (centerId: string, waiverId: string) =>
      api.get<DiveWaiver>(`/v1/divecenter/centers/${centerId}/waivers/${waiverId}`).then((r) => r.data),
    getMyWaivers: (params?: { centerId?: string; page?: number; limit?: number }) =>
      api.get<{ data: DiveWaiver[]; total: number }>('/v1/divecenter/my-waivers', { params }).then((r) => r.data).catch(() => ({ data: [], total: 0 })),
    getPolicy: (centerId: string) =>
      api.get<{ policy: MedicalPolicy }>(`/v1/divecenter/${centerId}/medical-policy`).then((r) => r.data.policy),
    updatePolicy: (centerId: string, data: Partial<MedicalPolicy>) =>
      api.put<{ policy: MedicalPolicy }>(`/v1/divecenter/${centerId}/medical-policy`, data).then((r) => r.data.policy),
  },

  incidents: {
    list: (centerId: string, params?: { status?: string; severity?: string; calendarEntryId?: string; from?: string; to?: string; page?: number; limit?: number }) =>
      api.get<{ data: IncidentReport[]; total: number }>(`/v1/divecenter/centers/${centerId}/incidents`, { params }).then((r) => r.data).catch(() => ({ data: [], total: 0 })),
    create: (centerId: string, data: { kind?: 'INCIDENT' | 'NEAR_MISS'; severity?: IncidentSeverity; title: string; description: string; occurredAt: string; calendarEntryId?: string; affectedUserId?: string; actionsTaken?: string; isMinorInvolved?: boolean; sosAlertId?: string; qaRefType?: 'SAFETY_INCIDENT' | 'NEAR_MISS'; qaRefId?: string }) =>
      api.post<IncidentReport>(`/v1/divecenter/centers/${centerId}/incidents`, data).then((r) => r.data),
    getProtocol: (centerId: string, incidentId: string) =>
      api.get<IncidentProtocolView>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/protocol`).then((r) => r.data),
    saveProtocol: (centerId: string, incidentId: string, protocol: Record<string, boolean>) =>
      api.put<IncidentProtocolView>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/protocol`, { protocol }).then((r) => r.data),
    saveLinks: (centerId: string, incidentId: string, data: { qaRefType?: 'SAFETY_INCIDENT' | 'NEAR_MISS' | null; qaRefId?: string | null; sosAlertId?: string | null; calendarEntryId?: string | null }) =>
      api.put(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/links`, data).then((r) => r.data),
    getMissingDiver: (centerId: string, incidentId: string) =>
      api.get<MissingDiverView>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/missing-diver`).then((r) => r.data),
    updateMissingDiver: (centerId: string, incidentId: string, data: { participantId?: string; participantName?: string; lastSeenAt?: string; lastSeenNote?: string; step?: { key: string; done: boolean; note?: string }; outcome?: string }) =>
      api.put<MissingDiverView>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/missing-diver`, data).then((r) => r.data),
    get: (centerId: string, incidentId: string) =>
      api.get<IncidentReport>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}`).then((r) => r.data),
    update: (centerId: string, incidentId: string, data: Partial<IncidentReport>) =>
      api.patch<IncidentReport>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}`, data).then((r) => r.data),
    transmitToAuthority: (centerId: string, incidentId: string, data?: { authorityProtocol?: string; authorityNotes?: string }) =>
      api.post<IncidentReport>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/transmit-authority`, data ?? {}).then((r) => r.data),
    reportToInail: (centerId: string, incidentId: string, data?: { inailProtocol?: string }) =>
      api.post<IncidentReport>(`/v1/divecenter/centers/${centerId}/incidents/${incidentId}/inail-report`, data ?? {}).then((r) => r.data),
  },

  safetyKits: {
    list: (centerId: string) =>
      api.get<SafetyKitCheck[]>(`/v1/divecenter/centers/${centerId}/safety-kits`).then((r) => r.data),
    create: (centerId: string, data: { kind: SafetyKitKind; checkedAt: string; nextCheckDue: string; notes?: string }) =>
      api.post<SafetyKitCheck>(`/v1/divecenter/centers/${centerId}/safety-kits`, data).then((r) => r.data),
  },

  checklists: {
    getTemplate: (centerId: string) =>
      api.get<{ items: SafetyChecklistItem[]; dm1996RequiredKeys: string[]; regulation: string }>(`/v1/divecenter/centers/${centerId}/checklists/templates`).then((r) => r.data),
    complete: (centerId: string, data: { calendarEntryId: string; items: SafetyChecklistItem[]; notes?: string }) =>
      api.post<{ dm1996Compliant: boolean }>(`/v1/divecenter/centers/${centerId}/checklists`, data).then((r) => r.data),
    forEntry: (centerId: string, entryId: string) =>
      api.get<{ exists: boolean; checklist?: { dm1996Compliant: boolean; items: SafetyChecklistItem[] }; template?: SafetyChecklistItem[]; auto?: Record<string, ChecklistAutoCheck>; dm1996RequiredKeys?: string[] }>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/checklist`).then((r) => r.data),
  },

  activityLog: {
    list: (centerId: string, params?: { limit?: number; offset?: number; type?: ActivityLogType; dateFrom?: string; dateTo?: string }) =>
      api.get<{ items: ActivityLog[]; total: number }>(`/v1/divecenter/centers/${centerId}/activity-log`, { params }).then((r) => r.data),
    create: (centerId: string, data: { type: ActivityLogType; description: string; date: string; authorName?: string; notes?: string }) =>
      api.post<ActivityLog>(`/v1/divecenter/centers/${centerId}/activity-log`, data).then((r) => r.data),
    delete: (centerId: string, id: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/activity-log/${id}`),
  },

  checkin: {
    verifyDiver: (centerId: string, token: string) =>
      api.post<{
        valid: boolean
        userId?: string
        firstName?: string | null
        lastName?: string | null
        certifications?: Array<{ agency: string; name: string; code: string; verificationStatus: string }>
        kycStatus?: string
        medicalExpiry?: string | null
        medicalCertWarning?: 'MISSING' | 'EXPIRED' | 'EXPIRING_SOON' | null
        medicalVerdict?: 'OK' | 'OPTIONAL_WITH_CERT' | 'RENEW_SUGGESTED' | 'MEDICAL_REQUIRED'
        certifiedDepthM?: number | null
        verifiedAt?: string
      }>(`/v1/divecenter/centers/${centerId}/verify-diver`, { token }).then((r) => r.data),
  },

  reviews: {
    list: (centerId: string, params?: { page?: number; limit?: number; sort?: string }) =>
      api.get<{ reviews: Review[]; total: number; page: number; limit: number }>(
        `/v1/divecenter/centers/${centerId}/reviews`, { params }
      ).then((r) => r.data),
    summary: (centerId: string) =>
      api.get<{ avgRating: number; reviewCount: number; breakdown: Record<number, number> }>(
        `/v1/divecenter/centers/${centerId}/reviews/summary`
      ).then((r) => r.data),
    create: (centerId: string, data: { rating: number; title?: string; body?: string; bookingId?: string }) =>
      api.post<Review>(`/v1/divecenter/centers/${centerId}/reviews`, data).then((r) => r.data),
    reply: (centerId: string, reviewId: string, reply: string) =>
      api.post(`/v1/divecenter/centers/${centerId}/reviews/${reviewId}/reply`, { reply }).then((r) => r.data),
    deleteOwn: (centerId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/reviews/me`),
  },

  // Scheda emergenza di un partecipante a un'immersione (staff/guida/co-partecipante).
  emergencyCard: (kind: 'excursion' | 'calendar', ref: string, userId: string) =>
    api.get<{
      userId: string
      shared: boolean
      firstName?: string | null
      lastName?: string | null
      displayName?: string | null
      bloodType?: string
      allergies?: Array<{ name: string }>
      currentMeds?: Array<{ name: string }>
      conditions?: Array<{ name: string }>
      diveRestrictions?: string[]
      emergencyNotes?: string | null
      danMember?: string | null
      emergencyContact?: { firstName: string; lastName: string; relationship: string; primaryPhone: string; secondaryPhone?: string | null } | null
      fitness?: { outcome: string; validUntil: string | null; expired: boolean; conditions: string | null } | null
    }>(`/v1/divecenter/dives/${kind}/${ref}/participants/${userId}/emergency-card`, { _skipToast: true } as any).then((r) => r.data),

  // Notifiche del centro (Blocco D): azioni-utente verso il centro, staff-only.
  notifications: {
    list: (centerId: string, params?: { limit?: number }) =>
      api.get<{ items: CenterNotification[]; total: number; unread: number }>(
        `/v1/divecenter/centers/${centerId}/notifications`, { params }
      ).then((r) => r.data),
    count: (centerId: string) =>
      api.get<{ unread: number }>(
        `/v1/divecenter/centers/${centerId}/notifications`, { params: { countOnly: true } }
      ).then((r) => r.data),
    markRead: (centerId: string) =>
      api.post<{ ok: boolean; seenAt: string }>(
        `/v1/divecenter/centers/${centerId}/notifications/mark-read`
      ).then((r) => r.data),
  },

  giftCards: {
    purchase: (centerId: string, data: {
      amountCents: number
      recipientEmail?: string
      message?: string
      expiryDays?: number
    }) =>
      api.post<GiftCard>(`/v1/divecenter/centers/${centerId}/gift-cards`, data).then((r) => r.data),

    listMine: () =>
      api.get<{ items: GiftCard[]; total: number }>('/v1/divecenter/me/gift-cards').then((r) => r.data),

    check: (code: string) =>
      api.get<{ code: string; centerId: string; amountCents: number; currency: string; status: string; expiresAt: string; valid: boolean }>(
        `/v1/divecenter/gift-cards/check/${code}`
      ).then((r) => r.data),

    redeem: (centerId: string, data: { code: string; bookingId?: string }) =>
      api.post<{ message: string; amountCents: number }>(`/v1/divecenter/centers/${centerId}/gift-cards/redeem`, data).then((r) => r.data),

    cancel: (centerId: string, gcId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/gift-cards/${gcId}`),
  },

  exams: {
    listQuestions: (centerId: string, courseId: string) =>
      api.get<ExamQuestion[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-questions`).then((r) => r.data),

    createQuestion: (centerId: string, courseId: string, data: {
      question: string; options: string[]; correctIdx: number; explanation?: string; order?: number
    }) =>
      api.post<ExamQuestion>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-questions`, data).then((r) => r.data),

    updateQuestion: (centerId: string, courseId: string, qId: string, data: Partial<{
      question: string; options: string[]; correctIdx: number; explanation: string; order: number
    }>) =>
      api.put<ExamQuestion>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-questions/${qId}`, data).then((r) => r.data),

    deleteQuestion: (centerId: string, courseId: string, qId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-questions/${qId}`),

    listSessions: (centerId: string, courseId: string) =>
      api.get<ExamSessionSummary[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-sessions`).then((r) => r.data),

    getSession: (centerId: string, courseId: string, sessionId: string) =>
      api.get<ExamSessionDetail>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-sessions/${sessionId}`).then((r) => r.data),

    startExam: (centerId: string, courseId: string) =>
      api.post<ExamStartResponse>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-sessions`).then((r) => r.data),

    submitExam: (centerId: string, courseId: string, sessionId: string, answers: { questionId: string; selectedIdx: number }[]) =>
      api.post<ExamResultResponse>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-sessions/${sessionId}/submit`, { answers }).then((r) => r.data),

    myExamSessions: (centerId: string, courseId: string) =>
      api.get<ExamSessionSummary[]>(`/v1/divecenter/centers/${centerId}/courses/${courseId}/exam-sessions/my`).then((r) => r.data),
  },

  courseAnalytics: (centerId: string, params?: { courseId?: string; year?: number }) =>
    api.get<CourseAnalyticsData>(`/v1/divecenter/centers/${centerId}/analytics/courses`, { params }).then((r) => r.data),

  calendar: {
    list: (centerId: string, params?: { startDate?: string; endDate?: string; status?: string; tripType?: string; page?: number; limit?: number }) =>
      api.get<{ data: CalendarEntry[]; total: number; page: number; limit: number }>(`/v1/divecenter/centers/${centerId}/calendar`, { params }).then((r) => r.data),
    get: (centerId: string, entryId: string) =>
      api.get<CalendarEntry>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}`).then((r) => r.data),
    create: (centerId: string, data: CreateCalendarEntryData) =>
      api.post<CalendarEntry>(`/v1/divecenter/centers/${centerId}/calendar`, data).then((r) => r.data),
    updateStatus: (centerId: string, entryId: string, data: { status: CalendarEntryStatus; postDiveNotes?: string }) =>
      api.patch<CalendarEntry>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/status`, data).then((r) => r.data),
    delete: (centerId: string, entryId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/calendar/${entryId}`),
    manifest: (centerId: string, entryId: string) =>
      api.get<BoatManifest>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/manifest`).then((r) => r.data),
    getPlan: (centerId: string, entryId: string) =>
      api.get<DivePlanResponse>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/plan`).then((r) => r.data),
    savePlan: (centerId: string, entryId: string, data: DivePlanData) =>
      api.put<DivePlanResponse>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/plan`, data).then((r) => r.data),
    getHandover: (centerId: string, entryId: string) =>
      api.get<BoatHandoverResponse>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/handover`).then((r) => r.data),
    saveHandover: (centerId: string, entryId: string, data: { items: Record<string, boolean>; captainName?: string | null; notes?: string | null }) =>
      api.put<BoatHandoverResponse>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/handover`, data).then((r) => r.data),
    signHandover: (centerId: string, entryId: string) =>
      api.patch<BoatHandoverResponse>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/handover/sign`).then((r) => r.data),
    getDebriefing: (centerId: string, entryId: string) =>
      api.get<Debriefing | null>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/debriefing`).then((r) => r.data),
    saveDebriefing: (centerId: string, entryId: string, data: { topics: Record<string, boolean>; notes?: string }) =>
      api.put<Debriefing>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/debriefing`, data).then((r) => r.data),
    addParticipants: (centerId: string, entryId: string, participants: CalendarParticipantInput[]) =>
      api.post<{ count: number; warnings?: RuleWarning[] }>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants`, { participants }).then((r) => r.data),
    onboardSafety: (centerId: string, entryId: string) =>
      api.get<OnboardSafety>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/onboard-safety`).then((r) => r.data),
    updateParticipant: (centerId: string, entryId: string, participantId: string, data: Partial<CalendarParticipantInput>) =>
      api.patch<CalendarParticipant>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}`, data).then((r) => r.data),
    removeParticipant: (centerId: string, entryId: string, participantId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}`),
    checkInParticipant: (centerId: string, entryId: string, participantId: string) =>
      api.patch<{ participant: CalendarParticipant; medicalWarning: string | null }>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}/checkin`, {}).then((r) => r.data),
    returnParticipant: (centerId: string, entryId: string, participantId: string) =>
      api.patch<CalendarParticipant>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}/return`, {}).then((r) => r.data),
    acknowledgeBriefing: (centerId: string, entryId: string, participantId: string) =>
      api.patch<CalendarParticipant>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}/acknowledge-briefing`, {}).then((r) => r.data),
    signEcoCode: (centerId: string, entryId: string, participantId: string, signedName: string) =>
      api.patch<CalendarParticipant>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}/eco-code/sign`, { signedName }).then((r) => r.data),
    checkEcoAwareness: (centerId: string, entryId: string, participantId: string) =>
      api.patch<CalendarParticipant>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/participants/${participantId}/eco-awareness`, {}).then((r) => r.data),
  },

  // ── Staff — direct hire & management ─────────────────────────────────
  staff: {
    list: (centerId: string, params?: { active?: boolean; role?: string }) =>
      api.get<StaffMember[]>(`/v1/divecenter/centers/${centerId}/staff`, { params }).then((r) => r.data),
    hire: (centerId: string, data: HireStaffData) =>
      api.post<StaffMember>(`/v1/divecenter/centers/${centerId}/staff`, data).then((r) => r.data),
    update: (centerId: string, staffId: string, data: { role?: StaffRoleType; additionalRoles?: StaffRoleType[]; startDate?: string; endDate?: string | null; isActive?: boolean }) =>
      api.patch<StaffMember>(`/v1/divecenter/centers/${centerId}/staff/${staffId}`, data).then((r) => r.data),
    terminate: (centerId: string, staffId: string, endDate?: string) =>
      api.patch<StaffMember>(`/v1/divecenter/centers/${centerId}/staff/${staffId}/terminate`, { endDate }).then((r) => r.data),
    remove: (centerId: string, staffId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/staff/${staffId}`).then((r) => r.data),
    transferOwnership: (centerId: string, newOwnerUserId: string) =>
      api.put<{ ok: boolean; message: string }>(`/v1/divecenter/centers/${centerId}/transfer-ownership`, { newOwnerUserId }).then((r) => r.data),
  },

  // ── Staff Requests ───────────────────────────────────────────────────
  // ── Pending Staff Contacts (external, pre-eDive) ─────────────────────
  pendingStaffContacts: {
    list: (centerId: string, status?: PendingStaffContactStatus) =>
      api.get<{ items: PendingStaffContact[]; total: number }>(
        `/v1/divecenter/centers/${centerId}/staff/external-contacts`,
        { params: { status } }
      ).then((r) => r.data),
    create: (centerId: string, data: {
      firstName: string
      lastName: string
      email?: string
      phone?: string
      requestedRole: StaffRoleType
      notes?: string
    }) =>
      api.post<PendingStaffContact>(
        `/v1/divecenter/centers/${centerId}/staff/external-contacts`,
        data
      ).then((r) => r.data),
    remove: (centerId: string, contactId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/staff/external-contacts/${contactId}`),
  },

  staffRequests: {
    list: (centerId: string, status?: string) =>
      api.get<StaffRequest[]>(`/v1/divecenter/centers/${centerId}/staff-requests`, { params: { status } }).then((r) => r.data),
    myRequests: () =>
      api.get<StaffRequest[]>('/v1/divecenter/my-staff-requests').then((r) => r.data),
    request: (centerId: string, requestedRole: string, note?: string) =>
      api.post<StaffRequest>(`/v1/divecenter/centers/${centerId}/staff-requests`, { requestedRole, note }).then((r) => r.data),
    invite: (centerId: string, userId: string, requestedRole: string, note?: string) =>
      api.post<StaffRequest>(`/v1/divecenter/centers/${centerId}/staff-requests/invite`, { userId, requestedRole, note }).then((r) => r.data),
    approve: (centerId: string, requestId: string) =>
      api.patch(`/v1/divecenter/centers/${centerId}/staff-requests/${requestId}/approve`).then((r) => r.data),
    reject: (centerId: string, requestId: string, note?: string) =>
      api.patch(`/v1/divecenter/centers/${centerId}/staff-requests/${requestId}/reject`, { note }).then((r) => r.data),
  },

  // ── Pacchetti corsi, classi programmate, studenti, certificazioni ─────
  //
  // Sette rotte servite dal centro e mai chiamate da nessuna schermata: il centro
  // poteva vendere corsi via API e non aveva un posto dove guardarli. Trovate
  // confrontando le rotte servite con quelle citate dalle interfacce
  // (scripts/e2e/funzioni-senza-interfaccia.mjs).
  coursePackages: {
    packages: (centerId: string, params?: { type?: string; active?: string; limit?: number }) =>
      api.get<{ packages: CoursePackage[]; total: number }>(`/v1/divecenter/${centerId}/packages`, { params })
        .then((r) => r.data),
    createPackage: (centerId: string, data: {
      name: string
      type?: string
      certificationAgency?: string
      price?: number
      currency?: string
      duration?: number
      maxStudents?: number
      includes?: string[]
      description?: string
      active?: boolean
    }) => api.post<CoursePackage>(`/v1/divecenter/${centerId}/packages`, data).then((r) => r.data),

    schedules: (centerId: string, params?: { status?: string; from?: string; to?: string; limit?: number }) =>
      api.get<{ schedules: CourseSchedule[]; total: number }>(`/v1/divecenter/${centerId}/schedules`, { params })
        .then((r) => r.data),
    createSchedule: (centerId: string, data: Record<string, unknown>) =>
      api.post(`/v1/divecenter/${centerId}/schedules`, data).then((r) => r.data),

    students: (centerId: string) =>
      api.get<{ students: CourseStudent[]; total: number }>(`/v1/divecenter/${centerId}/students`)
        .then((r) => r.data),
    certificationsIssued: (centerId: string) =>
      api.get<{ certifications: IssuedCertification[]; total: number }>(`/v1/divecenter/${centerId}/certifications-issued`)
        .then((r) => r.data),
    adminStats: () =>
      api.get<{ totalCourses: number; totalEnrollments: number; byLevel: { level: string; count: number }[] }>(
        '/v1/divecenter/admin/course-stats',
      ).then((r) => r.data),
  },

  // ── Center Affiliations ───────────────────────────────────────────────
  affiliations: {
    list: (centerId: string) =>
      api.get<CenterAffiliation[]>(`/v1/divecenter/centers/${centerId}/affiliations`).then((r) => r.data),
    submit: (centerId: string, data: { agencyCode: string; affiliationType: string; certNumber?: string; imageUrl: string; expiresAt?: string }) =>
      api.post<CenterAffiliation>(`/v1/divecenter/centers/${centerId}/affiliations`, data).then((r) => r.data),
  },

  // ── Excursions ────────────────────────────────────────────────────────
  excursions: {
    list: (centerId: string, params?: { status?: string; from?: string; to?: string }) =>
      api.get<Excursion[]>(`/v1/divecenter/centers/${centerId}/excursions`, { params }).then((r) => r.data),
    publicList: (params?: { country?: string; from?: string; to?: string; minCertLevel?: string; page?: number; limit?: number }) =>
      api.get<{ items: Excursion[]; total: number; pages: number }>('/v1/divecenter/excursions/public', { params }).then((r) => r.data),
    get: (centerId: string, id: string) =>
      api.get<Excursion>(`/v1/divecenter/centers/${centerId}/excursions/${id}`).then((r) => r.data),
    create: (centerId: string, data: CreateExcursionData) =>
      api.post<Excursion>(`/v1/divecenter/centers/${centerId}/excursions`, data).then((r) => r.data),
    update: (centerId: string, id: string, data: Partial<CreateExcursionData>) =>
      api.patch<Excursion>(`/v1/divecenter/centers/${centerId}/excursions/${id}`, data).then((r) => r.data),
    publish: (centerId: string, id: string) =>
      api.patch(`/v1/divecenter/centers/${centerId}/excursions/${id}/publish`).then((r) => r.data),
    cancel: (centerId: string, id: string, reason: string) =>
      api.patch(`/v1/divecenter/centers/${centerId}/excursions/${id}/cancel`, { reason }).then((r) => r.data),
    bookings: (centerId: string, id: string) =>
      api.get<ExcursionBooking[]>(`/v1/divecenter/centers/${centerId}/excursions/${id}/bookings`).then((r) => r.data),
    // Visibilità offerta (Blocco B)
    setAudience: (centerId: string, id: string, audience: 'PUBLIC' | 'RESTRICTED') =>
      api.patch<Excursion>(`/v1/divecenter/centers/${centerId}/excursions/${id}/audience`, { audience }).then((r) => r.data),
    invites: (centerId: string, id: string) =>
      api.get<ExcursionInvite[]>(`/v1/divecenter/centers/${centerId}/excursions/${id}/invites`).then((r) => r.data),
    addInvites: (centerId: string, id: string, userIds: string[]) =>
      api.post<ExcursionInvite[]>(`/v1/divecenter/centers/${centerId}/excursions/${id}/invites`, { userIds }).then((r) => r.data),
    removeInvite: (centerId: string, id: string, userId: string) =>
      api.delete(`/v1/divecenter/centers/${centerId}/excursions/${id}/invites/${userId}`).then((r) => r.data),
    // Diver actions
    book: (excursionId: string) =>
      api.post<ExcursionBooking>(`/v1/divecenter/excursions/${excursionId}/book`).then((r) => r.data),
    cancelBooking: (excursionId: string) =>
      api.delete(`/v1/divecenter/excursions/${excursionId}/book`).then((r) => r.data),
    myBookings: () =>
      api.get<ExcursionBooking[]>('/v1/divecenter/excursions/my-bookings').then((r) => r.data),
  },

  analytics: {
    kpi: (centerId: string, params?: { year?: number }) =>
      api.get<{
        year: number
        totalRevenueEuroCents: number
        bookingRevenueEuroCents: number
        courseRevenueEuroCents: number
        totalBookings: number
        certsIssued: number
        newCustomers: number
      }>(`/v1/divecenter/centers/${centerId}/analytics/kpi`, { params }).then((r) => r.data),

    revenue: (centerId: string, params?: { from?: string; to?: string }) =>
      api.get<{ data: { month: string; bookingRevenue: number; courseRevenue: number; total: number }[]; from: string; to: string }>(
        `/v1/divecenter/centers/${centerId}/analytics/revenue`, { params }
      ).then((r) => r.data.data),

    enrollments: (centerId: string, params?: { from?: string; to?: string }) =>
      api.get<{ data: { month: string; total: number; completed: number; byLevel: Record<string, number> }[]; from: string; to: string }>(
        `/v1/divecenter/centers/${centerId}/analytics/enrollments`, { params }
      ).then((r) => r.data.data),

    fillRate: (centerId: string, params?: { year?: number }) =>
      api.get<{ data: { month: string; fillRate: number | null }[]; year: number }>(
        `/v1/divecenter/centers/${centerId}/analytics/fill-rate`, { params }
      ).then((r) => r.data.data),
  },

  diveRegister: {
    list: (centerId: string, entryId: string) =>
      api.get<DiveRegisterEntry[]>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/dive-register`).then(r => r.data),
    create: (centerId: string, entryId: string, data: Partial<DiveRegisterEntry>) =>
      api.post<DiveRegisterEntry>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/dive-register`, data).then(r => r.data),
    update: (centerId: string, entryId: string, registerId: string, data: Partial<DiveRegisterEntry>) =>
      api.patch<DiveRegisterEntry>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/dive-register/${registerId}`, data).then(r => r.data),
    // G3 — l'operatore conferma di applicare il dato reale del computer (risolve discrepanza)
    applyDeviceData: (centerId: string, entryId: string, registerId: string) =>
      api.post<DiveRegisterEntry>(`/v1/divecenter/centers/${centerId}/calendar/${entryId}/dive-register/${registerId}/apply-device-data`).then(r => r.data),
    // G4 — resoconto del giorno: chi è in acqua / rientrato + stato riconciliazione
    today: (centerId: string, date?: string) =>
      api.get<DiveRegisterTodayReport>(`/v1/divecenter/centers/${centerId}/dive-register/today`, { params: date ? { date } : {} }).then(r => r.data),
    exportCsv: async (centerId: string, from?: string, to?: string) => {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const res = await api.get(`/v1/divecenter/centers/${centerId}/dive-register/export`, { params, responseType: 'blob' })
      const url = URL.createObjectURL(res.data as Blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `registro-immersioni-${centerId}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  },
}

export interface DiveCenterClaimRequest {
  id: string
  centerId: string
  center?: { id: string; name: string; city: string; country: string; claimStatus: string }
  requestedBy: string
  status: 'PENDING' | 'APPROVED' | 'REJECTED'
  note?: string | null
  reviewNote?: string | null
  reviewedBy?: string | null
  reviewedAt?: string | null
  createdAt: string
  updatedAt: string
}

export interface DiveRegisterEntry {
  id: string
  centerId: string
  calendarEntryId: string
  // ⚠️ Il partecipante si identifica in UNO di tre modi, e gli altri due sono null. Fino al
  // 06/08/2026 qui c'era `userId: string` non annullabile: una bugia, perche' il server
  // restituisce null per ogni ospite. TypeScript non poteva vedere il difetto, e la lista del
  // registro andava in errore (`null.slice(...)`) appena un centro registrava un ospite —
  // cioe' appena faceva la cosa giusta.
  userId: string | null
  preMemberId?: string | null
  participantName?: string | null
  guideName?: string | null
  guideSignature?: string | null
  certAgency?: string | null
  certCode?: string | null
  certNumber?: string | null
  checkInAt?: string | null
  plannedDepthM?: number | null
  gasType?: string | null
  checkOutAt?: string | null
  maxDepthAchievedM?: number | null
  gasActuallyUsed?: string | null
  notes?: string | null
  medicalWarning?: string | null
  regulatoryExportedAt?: string | null
  // Riconciliazione col logbook del sub (G3)
  logbookDiveId?: string | null
  logbookLinkedAt?: string | null
  deviceMaxDepthM?: number | null
  deviceDurationMin?: number | null
  discrepancyNote?: string | null
  createdAt: string
}

export interface DiveRegisterTodayReport {
  date: string
  counts: { total: number; inWater: number; returned: number; reconciled: number; discrepancies: number }
  inWater: DiveRegisterEntry[]
  returned: DiveRegisterEntry[]
  entries: DiveRegisterEntry[]
}

// ── Association membership ─────────────────────────────────────────────────────
export type AssociationType  = 'ASD' | 'APS' | 'ODV' | 'OTHER'
export type MembershipStatus = 'ACTIVE' | 'EXPIRED' | 'SUSPENDED' | 'PENDING'
export type PaymentMethod    = 'CASH' | 'TRANSFER' | 'ONLINE'
export type MembershipType   = 'ORDINARY' | 'HONORARY' | 'JUNIOR' | 'SUPPORTER'

export interface AssociationMembership {
  id:               string
  centerId:         string
  userId:           string
  membershipNumber: string | null
  status:           MembershipStatus
  joinedAt:         string
  expiresAt:        string | null
  feeEur:           number | null
  paidAt:           string | null
  paymentMethod:    PaymentMethod | null
  receiptNumber:    string | null
  membershipType:   MembershipType | null
  notes:            string | null
  invitedBy:        string | null
  createdAt:        string
  updatedAt:        string
  userEmail?:       string | null
  userPhone?:       string | null
  userName?:        string | null
  center?: {
    id: string; name: string; city: string; logoUrl: string | null; associationType: AssociationType | null
  }
}

export interface CreateMembershipData {
  userId:         string
  membershipType?: MembershipType
  feeEur?:        number
  paidAt?:        string
  paymentMethod?: PaymentMethod
  expiresAt?:     string
  notes?:         string
  status?:        'ACTIVE' | 'PENDING' | 'SUSPENDED'
}

export const membershipApi = {
  list: (centerId: string, status?: string, page = 1) =>
    api.get<{ data: AssociationMembership[]; total: number; page: number; pages: number }>(
      `/v1/divecenter/${centerId}/members`, { params: { status, page } }
    ).then(r => r.data),

  add: (centerId: string, data: CreateMembershipData) =>
    api.post<AssociationMembership>(`/v1/divecenter/${centerId}/members`, data).then(r => r.data),

  update: (centerId: string, memberId: string, data: Partial<CreateMembershipData & { receiptNumber: string }>) =>
    api.put<AssociationMembership>(`/v1/divecenter/${centerId}/members/${memberId}`, data).then(r => r.data),

  remove: (centerId: string, memberId: string) =>
    api.delete(`/v1/divecenter/${centerId}/members/${memberId}`),

  myMemberships: () =>
    api.get<{ data: AssociationMembership[] }>('/v1/divecenter/memberships/me').then(r => r.data.data),

  renew: (memberId: string) =>
    api.post<AssociationMembership>(`/v1/divecenter/memberships/renew/${memberId}`).then(r => r.data),

  joinRequest: (centerId: string) =>
    api.post<AssociationMembership & { center: { id: string; name: string; city: string; centerType: string; logoUrl: string | null } }>(
      `/v1/divecenter/${centerId}/join-request`
    ).then(r => r.data),

  notifyOne: (centerId: string, memberId: string, data: { subject: string; message: string }) =>
    api.post<{ sent: boolean; email: string }>(`/v1/divecenter/${centerId}/members/${memberId}/notify`, data).then(r => r.data),

  notifyAll: (centerId: string, data: { subject: string; message: string }) =>
    api.post<{ sent: number; total: number }>(`/v1/divecenter/${centerId}/members/notify-all`, data).then(r => r.data),
}

// ── Pre-members ───────────────────────────────────────────────────────────────

export type DcPreMemberStatus = 'PENDING' | 'RECONCILED'

export interface MatchCandidate {
  userId:      string
  reason:      'email' | 'phone' | 'name'
  score:       number
  matchedOn:   string
  displayName?: string | null
  userEmail?:  string | null
  userPhone?:  string | null
}

export interface DcPreMember {
  id:               string
  centerId:         string
  createdByUserId:  string
  firstName:        string
  lastName:         string
  email?:           string | null
  phone?:           string | null
  whatsappPhone?:   string | null
  notes?:           string | null
  status:           DcPreMemberStatus
  reconciledUserId?: string | null
  reconciledAt?:    string | null
  matchCandidates?: MatchCandidate[] | null
  createdAt:        string
}

export interface CreateDcPreMemberData {
  firstName:     string
  lastName:      string
  email?:        string
  phone?:        string
  whatsappPhone?: string
  notes?:        string
}

export const dcPreMembersApi = {
  list: (centerId: string) =>
    api.get<{ items: DcPreMember[]; total: number }>(`/v1/divecenter/${centerId}/pre-members`).then(r => r.data),

  create: (centerId: string, data: CreateDcPreMemberData) =>
    api.post<DcPreMember>(`/v1/divecenter/${centerId}/pre-members`, data).then(r => r.data),

  remove: (centerId: string, pmId: string) =>
    api.delete(`/v1/divecenter/${centerId}/pre-members/${pmId}`),

  reconcileAll: (centerId: string) =>
    api.post<{ reconciled: number; total: number }>(`/v1/divecenter/${centerId}/pre-members/reconcile-all`).then(r => r.data),

  reconcileManual: (centerId: string, pmId: string, userId: string) =>
    api.post<{ reconciled: boolean; userId: string }>(`/v1/divecenter/${centerId}/pre-members/${pmId}/reconcile-manual`, { userId }).then(r => r.data),

  approveMatch: (centerId: string, pmId: string, userId: string) =>
    api.post<{ reconciled: boolean; userId: string }>(`/v1/divecenter/${centerId}/pre-members/${pmId}/approve-match`, { userId }).then(r => r.data),

  dismissMatch: (centerId: string, pmId: string, userId: string) =>
    api.post<{ dismissed: boolean }>(`/v1/divecenter/${centerId}/pre-members/${pmId}/dismiss-match`, { userId }).then(r => r.data),
}

// ── Association Documents ─────────────────────────────────────────────────────

export type AssocDocType =
  | 'STATUTO' | 'REGOLAMENTO_SICUREZZA' | 'ATTO_COSTITUTIVO'
  | 'REGOLAMENTO_INTERNO' | 'POLIZZA_RC' | 'VERBALE_ASSEMBLEA' | 'ISCRIZIONE_REGISTRO'
  | 'BOAT_LICENSE' | 'LIABILITY_INSURANCE' | 'DIVE_CENTER_LICENSE'
  | 'FIRST_AID_CERT' | 'SAFETY_CERT' | 'TAX_DOCUMENT' | 'OTHER'

export interface CenterDocument {
  id: string
  centerId: string
  type: AssocDocType
  title: string
  fileKey?: string | null
  issuedAt?: string | null
  expiresAt?: string | null
  issuedBy?: string | null
  notes?: string | null
  version?: string | null
  approvedAt?: string | null
  uploadedBy: string
  createdAt: string
  updatedAt: string
}

export interface AssocDocRequired {
  type: AssocDocType
  present: boolean
  count: number
  latest: CenterDocument | null
}

export interface AssocDocStatus {
  required: AssocDocRequired[]
  allDocs: CenterDocument[]
}

export interface CreateDocumentData {
  type: AssocDocType
  title: string
  fileKey?: string
  issuedAt?: string
  expiresAt?: string
  issuedBy?: string
  notes?: string
  version?: string
  approvedAt?: string
}

export const associationDocsApi = {
  list: (centerId: string) =>
    api.get<AssocDocStatus>(`/v1/divecenter/centers/${centerId}/association-docs`).then(r => r.data),

  create: (centerId: string, data: CreateDocumentData) =>
    api.post<CenterDocument>(`/v1/divecenter/centers/${centerId}/documents`, data).then(r => r.data),

  remove: (centerId: string, docId: string) =>
    api.delete(`/v1/divecenter/centers/${centerId}/documents/${docId}`),
}

// ── Note comportamentali sui clienti (dietro flag CUSTOMER_NOTES_ENABLED) ────
export type CustomerNoteBasis = 'LEGITIMATE_INTEREST' | 'CONTRACT' | 'LEGAL_OBLIGATION'

export interface CustomerNote {
  id: string
  customerUserId: string
  note: string
  legalBasis: CustomerNoteBasis
  purpose: string
  retentionUntil: string
  createdAt: string
}

export const customerNotesApi = {
  status: () =>
    api.get<{ enabled: boolean }>('/v1/divecenter/customer-notes/status').then((r) => r.data),
  list: (centerId: string, customerUserId: string) =>
    api.get<CustomerNote[]>(`/v1/divecenter/centers/${centerId}/customer-notes`, { params: { customerUserId } }).then((r) => r.data),
  create: (centerId: string, data: { customerUserId: string; note: string; legalBasis: CustomerNoteBasis; purpose: string; retentionDays?: number }) =>
    api.post<CustomerNote>(`/v1/divecenter/centers/${centerId}/customer-notes`, data).then((r) => r.data),
  remove: (centerId: string, noteId: string) =>
    api.delete(`/v1/divecenter/centers/${centerId}/customer-notes/${noteId}`).then(() => undefined),
}

// ── Regole per giurisdizione (dati, non codice) ─────────────────────────────
export interface JurisdictionRule {
  code: string
  label: string
  waiverValidityDays: number | null
  medicalCertValidityMonths: number | null
  adultAgeYears: number | null
  diverFlagRequired: boolean | null
  plbRequired: boolean | null
  cylinderInspectionMonths: number | null
  nitroxAnalysisRequired: boolean | null
  notes: string | null
}

export type JurisdictionRuleInput = Omit<JurisdictionRule, 'code'>

export const jurisdictionApi = {
  list: () =>
    api.get<JurisdictionRule[]>('/v1/divecenter/jurisdiction-rules').then((r) => r.data),
  save: (code: string, data: JurisdictionRuleInput) =>
    api.put<JurisdictionRule>(`/v1/divecenter/jurisdiction-rules/${encodeURIComponent(code)}`, data).then((r) => r.data),
  forCenter: (centerId: string) =>
    api.get<{ jurisdictionCode: string; rule: JurisdictionRule | null }>(`/v1/divecenter/centers/${centerId}/jurisdiction`).then((r) => r.data),
  setForCenter: (centerId: string, jurisdictionCode: string) =>
    api.put<{ jurisdictionCode: string }>(`/v1/divecenter/centers/${centerId}/jurisdiction`, { jurisdictionCode }).then((r) => r.data),
}

// Profilo di agenzia predefinito per i limiti overhead (catalogo in planning); null = nessuno scelto.
export const planningSettingsApi = {
  get: (centerId: string) =>
    api.get<{ settings: { overheadProfileId: string | null } }>(`/v1/divecenter/${centerId}/planning-settings`).then((r) => r.data.settings),
  setOverheadProfile: (centerId: string, overheadProfileId: string | null) =>
    api.put<{ settings: { overheadProfileId: string | null } }>(`/v1/divecenter/${centerId}/planning-settings`, { overheadProfileId }).then((r) => r.data.settings),
}
